import { estimateBillingCredits, ceilBillingSeconds } from '~/utils/lipSyncBilling.js'

/** Anonymous guests: Dubbing 1.0 only, max duration (seconds). */
export const AI_DUBBING_ANON_MAX_SECONDS = 10

export const AI_DUBBING_COIN_TYPE = 'gold_coin'
export const AI_DUBBING_TASK_TYPE = 'dubbing'
export const AI_DUBBING_MAX_VIDEO_SIZE_MB = 100
/** Minimum uploaded video duration (seconds). */
export const AI_DUBBING_MIN_VIDEO_SECONDS = 5

export const DUBBING_MODELS = [
  {
    id: 'dubbing_1',
    generationType: 'dubbing1.0',
    name: 'Dubbing 1.0',
    tagline: 'Quick Turnaround · Basic Voice Clone',
    description: 'Fast dubbing with cloned voice for everyday clips.',
    resolutionLabel: '480p',
    creditPerSecond: 2,
    maxSeconds: 40,
    locked: false,
  },
  {
    id: 'dubbing_2',
    generationType: 'dubbing2.0',
    name: 'Dubbing 2.0',
    tagline: 'High Fidelity · Emotion Preserve · Lip-sync',
    description: 'Premium dubbing with emotion and lip-sync quality.',
    resolutionLabel: '720p',
    creditPerSecond: 5,
    maxSeconds: 120,
    locked: true,
  },
]

/**
 * Display list for target language.
 * `apiValue` is sent as `target_language`; `label` is UI English name.
 */
export const DUBBING_LANGUAGES = [
  {
    apiValue: 'English',
    label: 'English',
    nativeLabel: 'English',
    regionCode: 'US',
    flagCode: 'us',
  },
  {
    apiValue: 'Chinese',
    label: 'Chinese (Mandarin)',
    nativeLabel: '中文 (普通话)',
    regionCode: 'CN',
    flagCode: 'cn',
  },
  {
    apiValue: 'Spanish',
    label: 'Spanish',
    nativeLabel: 'Español',
    regionCode: 'ES',
    flagCode: 'es',
  },
  {
    apiValue: 'French',
    label: 'French',
    nativeLabel: 'Français',
    regionCode: 'FR',
    flagCode: 'fr',
  },
  {
    apiValue: 'German',
    label: 'German',
    nativeLabel: 'Deutsch',
    regionCode: 'DE',
    flagCode: 'de',
  },
  {
    apiValue: 'Japanese',
    label: 'Japanese',
    nativeLabel: '日本語',
    regionCode: 'JP',
    flagCode: 'jp',
  },
  {
    apiValue: 'Korean',
    label: 'Korean',
    nativeLabel: '한국어',
    regionCode: 'KR',
    flagCode: 'kr',
  },
  {
    apiValue: 'Portuguese',
    label: 'Portuguese',
    nativeLabel: 'Português',
    regionCode: 'PT',
    flagCode: 'pt',
  },
  {
    apiValue: 'Arabic',
    label: 'Arabic',
    nativeLabel: 'العربية',
    regionCode: 'SA',
    flagCode: 'sa',
  },
  {
    apiValue: 'Hindi',
    label: 'Hindi',
    nativeLabel: 'हिन्दी',
    regionCode: 'IN',
    flagCode: 'in',
  },
  {
    apiValue: 'Russian',
    label: 'Russian',
    nativeLabel: 'Русский',
    regionCode: 'RU',
    flagCode: 'ru',
  },
  {
    apiValue: 'Italian',
    label: 'Italian',
    nativeLabel: 'Italiano',
    regionCode: 'IT',
    flagCode: 'it',
  },
  {
    apiValue: 'Dutch',
    label: 'Dutch',
    nativeLabel: 'Nederlands',
    regionCode: 'NL',
    flagCode: 'nl',
  },
  {
    apiValue: 'Polish',
    label: 'Polish',
    nativeLabel: 'Polski',
    regionCode: 'PL',
    flagCode: 'pl',
  },
  {
    apiValue: 'Turkish',
    label: 'Turkish',
    nativeLabel: 'Türkçe',
    regionCode: 'TR',
    flagCode: 'tr',
  },
  {
    apiValue: 'Vietnamese',
    label: 'Vietnamese',
    nativeLabel: 'Tiếng Việt',
    regionCode: 'VN',
    flagCode: 'vn',
  },
  {
    apiValue: 'Thai',
    label: 'Thai',
    nativeLabel: 'ไทย',
    regionCode: 'TH',
    flagCode: 'th',
  },
  {
    apiValue: 'Indonesian',
    label: 'Indonesian',
    nativeLabel: 'Bahasa Indonesia',
    regionCode: 'ID',
    flagCode: 'id',
  },
]

export const DEFAULT_DUBBING_LANGUAGE = DUBBING_LANGUAGES[0].apiValue

export function dubbingModelById(modelId) {
  return DUBBING_MODELS.find((m) => m.id === modelId) || DUBBING_MODELS[0]
}

export function dubbingModelByGenerationType(generationType) {
  const gt = String(generationType || '').toLowerCase()
  if (gt.includes('2') || gt.includes('dubbing2')) return DUBBING_MODELS[1]
  return DUBBING_MODELS[0]
}

export function dubbingLanguageByApiValue(apiValue) {
  return (
    DUBBING_LANGUAGES.find((l) => l.apiValue === apiValue) || DUBBING_LANGUAGES[0]
  )
}

/** Effective max seconds for the current session (anon capped). */
export function dubbingMaxSeconds(model, isLoggedIn) {
  const modelMax = Number(model?.maxSeconds) || DUBBING_MODELS[0].maxSeconds
  if (isLoggedIn) return modelMax
  return Math.min(modelMax, AI_DUBBING_ANON_MAX_SECONDS)
}

/** Wall-clock estimate while generating: video duration × multiplier. */
export const DUBBING_GENERATION_TIME_MULTIPLIER = {
  dubbing_1: 20,
  dubbing_2: 50,
}

export function dubbingGenerationTimeMultiplier(modelOrId) {
  const model =
    typeof modelOrId === 'string' || modelOrId == null
      ? dubbingModelById(modelOrId)
      : modelOrId
  const id = model?.id || 'dubbing_1'
  return DUBBING_GENERATION_TIME_MULTIPLIER[id] || DUBBING_GENERATION_TIME_MULTIPLIER.dubbing_1
}

export function dubbingEstimatedGenerationSeconds(durationSeconds, modelOrId) {
  const duration = Math.max(0, Number(durationSeconds) || 0)
  const multiplier = dubbingGenerationTimeMultiplier(modelOrId)
  const estimated = duration * multiplier
  return estimated > 0 ? estimated : multiplier
}

export function dubbingCreditsForDuration(durationSeconds, creditPerSecond) {
  return estimateBillingCredits(durationSeconds, creditPerSecond)
}

export function buildDubbingCreatePayload({
  generationType,
  videoUrl,
  targetLanguage,
  duration,
}) {
  return {
    generation_type: generationType || DUBBING_MODELS[0].generationType,
    coin_type: AI_DUBBING_COIN_TYPE,
    video_url: String(videoUrl || '').trim(),
    target_language: String(targetLanguage || DEFAULT_DUBBING_LANGUAGE).trim(),
    duration: ceilBillingSeconds(duration),
  }
}

export function flagImageUrl(flagCode) {
  const code = String(flagCode || 'us').toLowerCase()
  return `https://flagcdn.com/w40/${code}.png`
}

export function isLikelyVideoUrl(url) {
  const path = String(url || '').split('?')[0].toLowerCase()
  return /\.(mp4|mov|webm|avi|mkv|m4v|mpeg|mpg)$/.test(path)
}

export function isLikelyAudioUrl(url) {
  const path = String(url || '').split('?')[0].toLowerCase()
  return /\.(mp3|wav|aac|m4a|ogg|amr|flac)$/.test(path)
}

/** Right-panel demo player (lazy-load video + dubbed audio tracks). */
export const AI_DUBBING_DEMO_POSTER =
  'https://static.lip-sync.ai/lipsync/ai-dubbing/demo_preview.webp'
export const AI_DUBBING_DEMO_VIDEO =
  'https://static.lip-sync.ai/lipsync/ai-dubbing/demo.mp4'
export const AI_DUBBING_DEMO_AUDIO = {
  en: 'https://static.lip-sync.ai/lipsync/ai-dubbing/demo_EN.mp3',
  de: 'https://static.lip-sync.ai/lipsync/ai-dubbing/demo_DE.mp3',
  pt: 'https://static.lip-sync.ai/lipsync/ai-dubbing/demo_PT.mp3',
}
export const AI_DUBBING_DEMO_LANGS = [
  { id: 'en', label: 'English' },
  { id: 'de', label: 'German' },
  { id: 'pt', label: 'Portuguese' },
]

/** Map site locale → demo dub track; fallback English. */
export function resolveAiDubbingDemoLang(locale) {
  const code = String(locale || '')
    .toLowerCase()
    .split(/[-_]/)[0]
  if (code === 'de' || code === 'pt' || code === 'en') return code
  return 'en'
}

export function formatDubbingClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
